import type {
  Address,
  ApplicationStatusResponse,
  DealSummarySection,
  DscApplicantSection,
  DscWorkbenchDashboardResponse,
  ResidenceSection,
} from "@credit-acceptance/step6-bff-client";

import type { DealIntelligenceCardViewModel, DealTier, SignerSummary } from "./DealIntelligenceCard.types";

// ---------------------------------------------------------------------------
// dealIntelligenceCard.mapping
//
// Pure mapper: DscWorkbenchDashboardResponse → DealIntelligenceCardViewModel.
// No React. Unit-testable and side-effect-free.
// ---------------------------------------------------------------------------

// BFF address-type id: 1 = CURRENT (physical).
const PHYSICAL_ADDRESS_TYPE_ID = 1;

// Known program tiers, keyed by the uppercased scoreProgram string. The BFF
// derives `dealSummary.scoreProgram` server-side from the CAS
// scoringProgramTypeId (parity with dealer-ui's env-based PROGRAM_STATUS map),
// so we only normalise and look it up here. Anything unknown/absent falls back
// to STANDARD (the base tier), which renders no badge.
const TIER_BY_SCORE_PROGRAM: Record<string, DealTier> = {
  STANDARD: "STANDARD",
  SILVER: "SILVER",
  GOLD: "GOLD",
  PLATINUM: "PLATINUM",
};

/**
 * Maps the BFF `dealSummary.scoreProgram` string onto a `DealTier`. Case- and
 * whitespace-insensitive; unrecognised or missing values map to "STANDARD".
 * @param {string | undefined} scoreProgram - The scoreProgram from dealSummary.
 * @returns {DealTier} The resolved tier (defaults to "STANDARD").
 */
export function mapScoreProgramToTier(scoreProgram: string | undefined): DealTier {
  if (!scoreProgram) {
    return "STANDARD";
  }
  return TIER_BY_SCORE_PROGRAM[scoreProgram.trim().toUpperCase()] ?? "STANDARD";
}

/**
 * Formats the dealer summary into the card's combined "Name (ID)" display, e.g.
 * "Digital Contracting of Illinois (1TL)". Falls back to the name alone when the
 * dealer id is absent, and to an empty string when no dealer name is available.
 * @param {DealSummarySection | undefined} dealSummary - The BFF deal summary section.
 * @returns {string} The combined dealer display string, or "".
 */
function formatDealer(dealSummary: DealSummarySection | undefined): string {
  const name = dealSummary?.dealerName?.trim();
  const id = dealSummary?.dealerId?.trim();
  if (!name) {
    return "";
  }
  return id ? `${name} (${id})` : name;
}

/**
 * Formats the application status + phase into the card's combined "Status
 * (phase)" display, e.g. "Pending Deal (New Deal Review)". Falls back to the
 * status alone when the phase is absent, and to an empty string when no status
 * description is available. Sourced from the ApplicationStatus BFF endpoint
 * (separate from the dashboard).
 * @param {ApplicationStatusResponse | undefined} status - The BFF application status response.
 * @returns {string} The combined status display string, or "".
 */
function formatStatus(status: ApplicationStatusResponse | undefined): string {
  const statusDescription = status?.statusDescription?.trim();
  const phaseDescription = status?.phaseDescription?.trim();
  if (!statusDescription) {
    return "";
  }
  return phaseDescription ? `${statusDescription} (${phaseDescription})` : statusDescription;
}

/** USD formatter shared by all advance amounts produced by this module. */
const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

/**
 * Formats a raw ISO date string (YYYY-MM-DD) into the display format MM/DD/YYYY.
 * Returns an empty string when the input is absent or not a valid date string.
 * @param {string | undefined} iso - An ISO date string such as "1985-01-15".
 * @returns {string} The formatted date, e.g. "01/15/1985", or "".
 */
function formatDisplayDate(iso: string | undefined): string {
  if (!iso) {
    return "";
  }
  const parts = iso.split("-");
  if (parts.length !== 3) {
    return "";
  }
  const [year, month, day] = parts;
  return `${month}/${day}/${year}`;
}

/**
 * Finds the physical/CURRENT address from an address array. Matches by the
 * human-readable `addressType === "PHYSICAL"` string first, with a fallback to
 * the numeric `addressTypeId === 1` (CURRENT) for records that omit the string.
 * @param {Address[] | undefined} addresses - The residence addresses.
 * @returns {Address | undefined} The physical address, if found.
 */
function findPhysicalAddress(addresses: Address[] | undefined): Address | undefined {
  return addresses?.find(
    (address) => address.addressType === "PHYSICAL" || address.addressTypeId === PHYSICAL_ADDRESS_TYPE_ID
  );
}

/**
 * Builds the first address display line (street number + street name + apt).
 * Returns an empty string when none of the parts are present.
 * @param {Address | undefined} physical - The physical/CURRENT address record.
 * @returns {string} Formatted address line 1, e.g. "123 Main St, Apt 4B".
 */
function buildAddressLine1(physical: Address | undefined): string {
  if (!physical) {
    return "";
  }
  const street = [physical.streetNumber, physical.streetName].filter(Boolean).join(" ");
  const apt = physical.apartmentNumber ? `, Apt ${physical.apartmentNumber}` : "";
  return `${street}${apt}`;
}

/**
 * Builds the second address display line (city, state zip).
 * Returns an empty string when the address record is absent.
 * @param {Address | undefined} physical - The physical/CURRENT address record.
 * @returns {string} Formatted address line 2, e.g. "Austin, TX 78701".
 */
function buildAddressLine2(physical: Address | undefined): string {
  if (!physical) {
    return "";
  }
  const cityState = [physical.city, physical.state].filter(Boolean).join(", ");
  const zip = physical.zip ?? "";
  return cityState && zip ? `${cityState} ${zip}` : cityState || zip;
}

/**
 * Picks the primary phone number from the residence phones array. Phones are
 * ordered by `displayOrder` (ascending); the first one is the primary. Returns
 * an empty string when no phones are recorded.
 * @param {ResidenceSection["phones"]} phones - The applicant's phone records.
 * @returns {string} The primary phone number string, or "".
 */
function pickPrimaryPhone(phones: ResidenceSection["phones"]): string {
  if (!phones || phones.length === 0) {
    return "";
  }
  const sorted = [...phones].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  return sorted[0].number ?? "";
}

/**
 * Builds the full display name from the residence section name parts.
 * Joins non-empty parts in order: firstName middleName lastName nameSuffix.
 * @param {ResidenceSection} residence - The applicant's residence section.
 * @returns {string} The display name, e.g. "John A Smith Jr".
 */
function buildFullName(residence: ResidenceSection): string {
  return [residence.firstName, residence.middleName, residence.lastName, residence.nameSuffix]
    .filter(Boolean)
    .join(" ");
}

/**
 * Maps a single `ResidenceSection` onto a `SignerSummary` for the Deal
 * Intelligence Card header. Returns a blank signer summary when the residence
 * section is absent so callers never need to null-check downstream.
 * @param {ResidenceSection | undefined} residence - The applicant's residence section.
 * @returns {SignerSummary} The signer display data.
 */
function mapResidenceToSignerSummary(residence: ResidenceSection | undefined): SignerSummary {
  if (!residence) {
    return { name: "", ssn: "—", dob: "", addressLine1: "", addressLine2: "", phone: "" };
  }

  const physical = findPhysicalAddress(residence.addresses);

  return {
    name: buildFullName(residence),
    // ssnOrItin is the resolved SSN/ITIN value from Customer Hub (renamed from
    // `ssn` in step6-bff-client@1.0.287). Show "—" when undefined/absent (no
    // record on file). The view-model field stays `ssn` — this card labels it SSN.
    ssn: residence.ssnOrItin ?? "—",
    dob: formatDisplayDate(residence.dateOfBirth),
    addressLine1: buildAddressLine1(physical),
    addressLine2: buildAddressLine2(physical),
    phone: pickPrimaryPhone(residence.phones),
  };
}

/**
 * Formats a numeric advance amount as a USD currency string.
 * Returns an empty string when the value is absent or not a finite number.
 * @param {number | undefined} amount - The raw advance amount from dealStatus.
 * @returns {string} Formatted currency string, e.g. "$26,843.95", or "".
 */
function formatAdvance(amount: number | undefined): string {
  if (amount === undefined || amount === null || !Number.isFinite(amount)) {
    return "";
  }
  return usdFormatter.format(amount);
}

/**
 * Determines whether the second signer is present on the deal. Gates on
 * `DscApplicantSection.present` (boolean flag added in step6-bff-client@1.0.229).
 * Falls back to checking whether a residence section exists when `present` is
 * not explicitly set, to handle older BFF responses gracefully.
 * @param {DscApplicantSection | undefined} secondSigner - The second signer section.
 * @returns {boolean} True when the second signer should be shown.
 */
function isSecondSignerPresent(secondSigner: DscApplicantSection | undefined): boolean {
  if (!secondSigner) {
    return false;
  }
  // Prefer the explicit `present` flag; fall back to residence existence.
  if (typeof secondSigner.present === "boolean") {
    return secondSigner.present;
  }
  return !!secondSigner.residence;
}

/**
 * Maps a `DscWorkbenchDashboardResponse` to a `DealIntelligenceCardViewModel`
 * for the Deal Intelligence Card. The mapper is pure (no React, no side
 * effects) and safe to call with a partial or empty response object.
 * @param {DscWorkbenchDashboardResponse} response - The BFF dashboard response.
 * @param {string} approvalId - The deal approval ID (from the URL, not the response,
 * because `response.approvalId` may be absent on partial failures).
 * @param {ApplicationStatusResponse | undefined} status - The application status + phase
 * from the ApplicationStatus endpoint (a separate read). Absent while that call is
 * pending or when it fails, in which case the status renders empty.
 * @returns {DealIntelligenceCardViewModel} The mapped view model.
 */
export function mapDashboardToDealIntelligenceCard(
  response: DscWorkbenchDashboardResponse,
  approvalId: string,
  status?: ApplicationStatusResponse
): DealIntelligenceCardViewModel {
  const driver = mapResidenceToSignerSummary(response.driver?.residence);
  const secondSignerPresent = isSecondSignerPresent(response.secondSigner);
  const secondSigner = secondSignerPresent ? mapResidenceToSignerSummary(response.secondSigner?.residence) : undefined;

  return {
    approvalId,
    tier: mapScoreProgramToTier(response.dealSummary?.scoreProgram),
    // Deal status + phase come from the ApplicationStatus endpoint (separate
    // read), combined into a single "Status (phase)" display.
    status: formatStatus(status),
    driver,
    secondSigner,
    dealerName: formatDealer(response.dealSummary),
    advance: formatAdvance(response.dealStatus?.dealerStatementAdvance),
  };
}
